/**
 * "Create Pipeline Job" on an accepted Pipeline quote (PF-5c, fieldiq-api#1232).
 *
 * NDT's Create NDT Job, plus the one thing a Pipeline job needs that an NDT job
 * does not: a SITE (pf_jobs.site_id is NOT NULL). The site list is the quote
 * customer's sites (GET /pipeline-facility/sites?customerId=); the segment list
 * is that site's segments, optional, and offered only for a pipeline that has
 * some. The day is optional, as on NDT's.
 *
 * A press with no site is SAID, not sent: the API's SITE_REQUIRED sentence, in
 * the quote screen's refusal notice. Every API refusal goes the same way
 * (pfQuoteRefusal), and success hands the job to the screen, which opens it —
 * a repeat (alreadyCreated) opens the job that already exists.
 *
 * Shown by QuoteDetailScreen only on an accepted Pipeline quote, only to admin
 * and office staff (the door's requireRole), and only once created-records says
 * the quote has no Pipeline job yet.
 */
import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Loader2 } from 'lucide-react';
import { api } from '../../api/client';
import { usePfSite, usePfSites } from '../../hooks/usePipelineFacility';
import { pfQuoteRefusal } from '../../lib/pf-quotes';
import type { CreatePfJobResponse } from '../../types/quote-pricing';

const SELECT = 'bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white max-w-[16rem]';
const ACTION_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:opacity-50 disabled:cursor-not-allowed';

export function CreatePfJobAction({
  quoteId,
  customerId,
  onCreated,
  onRefused,
}: {
  quoteId: string;
  customerId: string | null | undefined;
  onCreated: (data: CreatePfJobResponse['data']) => void;
  onRefused: (refusal: { code?: string; error: string }) => void;
}) {
  const [siteId, setSiteId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [day, setDay] = useState('');

  const create = useMutation({
    mutationFn: () => api.quotes.createPfJob(quoteId, {
      siteId,
      ...(segmentId ? { segmentId } : {}),
      ...(day ? { scheduledDate: day } : {}),
    }),
    onSuccess: (res) => {
      const d = res.data?.data;
      if (d?.job?.id) onCreated(d);
    },
    onError: (err) => onRefused(pfQuoteRefusal(err, 'Failed to create the Pipeline job')),
  });

  const press = () => {
    // No customer on the quote: the API's QUOTE_CUSTOMER_REQUIRED is the reason, so ask it.
    if (!siteId && customerId) {
      onRefused(pfQuoteRefusal({ response: { status: 400, data: { code: 'SITE_REQUIRED' } } }, ''));
      return;
    }
    create.mutate();
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2" data-testid="create-pf-job">
      {customerId && (
        <SiteAndSegment
          customerId={customerId}
          siteId={siteId}
          segmentId={segmentId}
          onSite={(id) => { setSiteId(id); setSegmentId(''); }}
          onSegment={setSegmentId}
        />
      )}
      <input
        type="date"
        aria-label="Pipeline job day (optional)"
        value={day}
        onChange={(e) => setDay(e.target.value)}
        className="bg-fieldiq-dark border border-fieldiq-border rounded-lg px-2 py-1.5 text-sm text-white"
      />
      <button onClick={press} disabled={create.isPending} className={ACTION_PRIMARY}>
        {create.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle size={15} />}
        Create Pipeline Job
      </button>
    </span>
  );
}

/** The quote customer's sites, then the picked pipeline's segments (optional). Mounted only with a customer. */
function SiteAndSegment({
  customerId,
  siteId,
  segmentId,
  onSite,
  onSegment,
}: {
  customerId: string;
  siteId: string;
  segmentId: string;
  onSite: (id: string) => void;
  onSegment: (id: string) => void;
}) {
  const sites = usePfSites({ customerId });
  const siteRows = sites.data?.data ?? [];
  const site = usePfSite(siteId || undefined);
  const segments = site.data?.data?.kind === 'pipeline' ? site.data.data.segments ?? [] : [];
  const empty = sites.isLoading ? 'Loading sites…' : sites.isError ? 'Sites could not be loaded' : siteRows.length ? 'Choose the site…' : 'No sites for this customer';
  return (
    <>
      <select aria-label="Pipeline job site" value={siteId} onChange={(e) => onSite(e.target.value)} className={SELECT}>
        <option value="">{empty}</option>
        {siteRows.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}{s.status && s.status !== 'active' ? ` (${s.status})` : ''}
          </option>
        ))}
      </select>
      {segments.length > 0 && (
        <select aria-label="Pipeline job segment (optional)" value={segmentId} onChange={(e) => onSegment(e.target.value)} className={SELECT}>
          <option value="">Whole line</option>
          {segments.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label || `${g.fromChainage ?? '—'}–${g.toChainage ?? '—'}`}
            </option>
          ))}
        </select>
      )}
    </>
  );
}
